import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import CommonButton from "../../components/widgets/common_button";
import JobsService from "../../service/jobs.service";
import Toaster from "../../components/common/toaster";
import {
  ArrowLeft,
  Briefcase,
  AlertTriangle,
  MapPin,
  User,
  FileText,
  Trash2,
} from "lucide-react";

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [statusChanging, setStatusChanging] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);

  const capitalize = (text) => {
    if (typeof text !== "string" || text.length === 0) return "-";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Get current user's role from localStorage
  const getCurrentUserRole = () => {
    try {
      const adminStore = localStorage.getItem("admin_store");
      if (adminStore) {
        const userData = JSON.parse(adminStore);
        return userData.role;
      }
    } catch (error) {
      console.error("Error parsing admin store:", error);
    }
    return null;
  };

  // Determine the correct back route based on current user role
  const getBackRoute = () => {
    const currentUserRole = getCurrentUserRole();

    // If we came from a specific page, go back to that page
    if (location.state?.from) {
      return location.state.from;
    }

    // Otherwise, determine based on current user role
    switch (currentUserRole) {
      case "job_seekers":
        return "/jobs"; // Job seekers should go back to jobs page
      case "employers":
        return "/jobs"; // Employers should go back to jobs page
      default:
        return "/jobs"; // Default fallback
    }
  };

  const imagePlaceholder =
    "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

  // Get job data from navigation state or redirect if not available
  useEffect(() => {
    const jobFromState = location.state?.job;
    if (jobFromState) {
      setJob(jobFromState);
    } else {
      // If no job data in state, redirect back to jobs list
      Toaster("error", "Job data not found. Redirecting to jobs list.");
      navigate("/jobs");
    }
  }, [location.state, navigate]);

  const handleStatusChange = async (value) => {
    setStatusChanging(true);
    try {
      // Assuming there's a toggle job status API
      // await JobsService.toggleJobStatus(jobId);

      // Update local state
      setJob((prev) => ({
        ...prev,
        status: value === "active" ? "active" : "pause",
      }));

      Toaster(
        "success",
        `Job ${value === "active" ? "activated" : "paused"} successfully`
      );
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to update job status"
      );
    } finally {
      setStatusChanging(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleDeleteJob = async () => {
    setDeletingJob(true);
    try {
      await JobsService.deleteJob(jobId);

      Toaster("success", "Job deleted successfully");

      // Navigate back to jobs list
      navigate(getBackRoute());
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to delete job"
      );
    } finally {
      setDeletingJob(false);
      setDeleteDialogOpen(false);
    }
  };

  const formatText = (text) => {
    if (!text) return "-";
    return text
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getJobCategoryIcon = (category) => {
    // Normalize category name to handle different formats
    const normalizedCategory = category?.toLowerCase().replace(/[\s&]/g, "_");

    const categoryIcons = {
      entry_level_jobs: require("../../assets/icon/Entry.png"),
      deliveries_distribution: require("../../assets/icon/Distribution.png"),
      maintenance_cleaning: require("../../assets/icon/Cleaning.png"),
      warehouse_packing: require("../../assets/icon/Packing.png"),
      food_service: require("../../assets/icon/Food.png"),
      customer_service: require("../../assets/icon/Customer.png"),
      sales: require("../../assets/icon/Sales.png"),
      drivers_couriers: require("../../assets/icon/Drivers.png"),
      physical_labor: require("../../assets/icon/Physical.png"),
      technicians: require("../../assets/icon/Technician.png"),
      temporary_jobs: require("../../assets/icon/Temporary.png"),
      service_providers: require("../../assets/icon/Service.png"),
      // Alternative mappings
      "entry-level jobs": require("../../assets/icon/Entry.png"),
      "deliveries & distribution": require("../../assets/icon/Distribution.png"),
      "maintenance & cleaning": require("../../assets/icon/Cleaning.png"),
      "warehouse & packing": require("../../assets/icon/Packing.png"),
      "food service": require("../../assets/icon/Food.png"),
      "customer service": require("../../assets/icon/Customer.png"),
      "drivers & couriers": require("../../assets/icon/Drivers.png"),
      "physical labor": require("../../assets/icon/Physical.png"),
      "temporary jobs": require("../../assets/icon/Temporary.png"),
      "service providers": require("../../assets/icon/Service.png"),
    };
    return (
      categoryIcons[category] ||
      categoryIcons[normalizedCategory] ||
      require("../../assets/icon/Entry.png")
    );
  };

  const getJobCategoryName = (category) => {
    const categoryNames = {
      entry_level_jobs: "Entry-Level Jobs",
      deliveries_and_distribution: "Deliveries and Distribution",
      maintenance_and_cleaning: "Maintenance and Cleaning",
      warehouse_and_packing: "Warehouse and Packing",
      food_service: "Food Service",
      customer_service: "Customer Service",
      sales: "Sales",
      drivers_and_couriers: "Drivers and Couriers",
      physical_labor: "Physical Labor",
      technicians: "Technicians",
      temporary_jobs: "Temporary Jobs",
      service_providers: "Service Providers",
    };
    return categoryNames[category] || formatText(category);
  };

  if (!job) {
    return (
      <div className="max-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Job Not Found
          </h2>
          <p className="text-gray-500 mb-6">
            The requested job could not be found.
          </p>
          <CommonButton
            variant="outline"
            onClick={() => navigate(getBackRoute())}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </CommonButton>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-screen bg-gray-50">
      <div className="max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate(getBackRoute())}
            className="flex items-center gap-2 h-10"
          >
            <ArrowLeft className="h-6 w-6" />
            <span className="text-lg">Back</span>
          </Button>
        </div>

        {/* Main Card */}
        <Card className="p-8 relative">
          {/* Delete Button - Top Right Corner of Card */}
          <button
            onClick={() => setDeleteDialogOpen(true)}
            className="absolute top-4 right-4 p-2 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-200 shadow-lg z-10"
            title="Delete Job"
          >
            <Trash2 className="h-4 w-4" />
          </button>

          {/* Job Image and Title Section */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Side - Full Job Image */}
              <div className="lg:w-1/2">
                <img
                  src={job.jobImg || imagePlaceholder}
                  alt={capitalize(job?.title)}
                  className="w-full h-64 sm:h-72 lg:h-80 object-contain rounded-xl"
                />
              </div>

              {/* Right Side - Job Title and Creator */}
              <div className="lg:w-1/2 flex flex-col justify-end">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words mb-4">
                  {capitalize(job?.title)}
                </h1>
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-5 h-5" />
                  <span className="text-base">
                    Created by: {capitalize(job?.createdBy?.firstName)}{" "}
                    {capitalize(job?.createdBy?.lastName)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 mb-6"></div>

          {/* Job Details Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 bg-gradient-to-b from-green-500 to-blue-600 rounded-full"></div>
              <h2 className="text-lg font-semibold text-gray-900">
                Job Information
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Location Card */}
              <div className="group">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100  ">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Location
                    </h3>
                  </div>
                  <p className="text-sm text-gray-700 font-medium break-words">
                    {capitalize(job?.location)}
                  </p>
                </div>
              </div>

              {/* Description Card */}
              {job.description && (
                <div className="group">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100 ">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">
                        Description
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 break-words whitespace-normal">
                      {capitalize(job?.description)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Categories Section */}
          {job.jobCategories && job.jobCategories.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-4 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Ads Categories
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {job.jobCategories.map((category, index) => (
                  <div
                    key={index}
                    className="group bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200 hover:shadow-md transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <img
                          src={getJobCategoryIcon(category)}
                          alt={getJobCategoryName(category)}
                          className="w-5 h-5 object-contain"
                          style={{
                            imageRendering: "pixelated",
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-900 group-hover:text-gray-700 transition-colors duration-300">
                          {getJobCategoryName(category)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to{" "}
              {job.status === "active" ? "pause" : "activate"} this job?
              {job.status === "active" ? (
                <span className="block mt-2 text-yellow-600">
                  This will pause the job and it won't be visible to job
                  seekers.
                </span>
              ) : (
                <span className="block mt-2 text-green-600">
                  This will activate the job and make it visible to job seekers.
                </span>
              )}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <CommonButton
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant={job.status === "active" ? "default" : "default"}
              onClick={() =>
                handleStatusChange(job.status === "active" ? "pause" : "active")
              }
              disabled={statusChanging}
            >
              {statusChanging ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                `${job.status === "active" ? "Pause" : "Activate"} Job`
              )}
            </CommonButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete Job
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this job?
            </p>
            {job && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">
                  {capitalize(job?.title)}
                </p>
                <p className="text-sm text-gray-600">
                  Created by: {capitalize(job?.createdBy?.firstName)}
                </p>
              </div>
            )}
            <p className="text-sm text-red-600 mt-3">
              This action cannot be undone. The job will be permanently deleted.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <CommonButton
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="destructive"
              onClick={handleDeleteJob}
              disabled={deletingJob}
            >
              {deletingJob ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                "Delete Job"
              )}
            </CommonButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetails;
